import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTE_BENEFICIARY_SERVICES } from "../navigation/routes";
import { Button, HStack, Icon, VStack, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useFake } from "../hooks";
import { Trans, useTranslation } from "react-i18next";
import BeneficiaryForm from "../components/beneficiary/BeneficiaryForm";
import VehicleForm from "../components/beneficiary/VehicleForm";
import InterventionAddressForm from "../components/beneficiary/InterventionAddressForm";
import ContractForm from "../components/beneficiary/ContractForm";
import FileForm from "../components/beneficiary/FileForm";
import { PrimaryCard } from "../components/common/cards";
import CallerForm from "../components/beneficiary/CallerForm";
import ObservationForm from "../components/beneficiary/ObservationForm";
import AiForm from "../components/beneficiary/AiForm";
import { useAssistance } from "../plugin/hooks";
import VictimForm from "../components/beneficiary/VictimForm";
import UserIcon from "../plugin/components/common/icons/UserIcon"
import InterventionIcon from "../plugin/components/common/icons/InterventionIcon";
import ObservationIcon from "../plugin/components/common/icons/ObservationIcon";
import IntelligentIcon from "../plugin/components/common/icons/IntelligentIcon";
import VictimIcon from "../plugin/components/common/icons/VictimIcon";
import { CarIcon } from "../plugin/components/common/icons";
import ContractIcon from "../plugin/components/common/icons/ContractIcon";
import FileIcon from "../plugin/components/common/icons/FileIcon";

/**
 * Renders the beneficiary file page
 * 
 * @returns The beneficiary file page
 */
const BeneficiaryFilePage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { beneficiaryId } = useParams();
  const { assistance: remoteAssistance } = useAssistance()
  const { getAssistanceFromHistory, updateAssistanceFromHistory } = useFake();
  const assistance = useMemo(
    () => getAssistanceFromHistory(beneficiaryId),
    [beneficiaryId, getAssistanceFromHistory],
  );
  const formErrorToastId = "form-error-toast-id";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm();

  const navigate = useNavigate();
  const { setMagicPasteCallback } = useFake();

  /**
   * Handles the form submission
   * 
   * @param {Object} values The form values
   */
  const onSubmit = useCallback(
    (values) =>
      new Promise((resolve) => {
        updateAssistanceFromHistory({
          ...assistance,
          data: values,
        });
        navigate(ROUTE_BENEFICIARY_SERVICES(beneficiaryId));
        resolve();
      }),
    [assistance, beneficiaryId, navigate, updateAssistanceFromHistory],
  );

  /**
   * Handles the form submission error
   * 
   * @returns The form submission error
   */
  const onSubmitError = useCallback(
    () =>
      new Promise((resolve) => {
        if (!toast.isActive(formErrorToastId)) {
          toast({
            id: formErrorToastId,
            title: t("beneficiaryFile.form.invalid.title"),
            description: t("beneficiaryFile.form.invalid.description"),
            position: "top",
            status: "error",
          });
        }
        resolve();
      }),
    [t, toast],
  );

  /**
   * Handles the magic paste item
   * 
   * @param {Object} element The element
   * @param {String} key The key
   * @param {String} value The value
   */
  const handleMagicPasteItem = useCallback(async (element, key, value, colorize) => {
    if (key === "ai") {
      if (colorize && (value || value === 0)) {
        element.style.borderWidth = "1px";
        element.style.background = "linear-gradient(#54C0E830, #9747FF30)";
        element.style.borderColor = "#744AAB";
      }

      const words = value?.split(/[ \t]+/);
      let content = "";

      if (words) {
        element.style.color = "white";
        for (const word of words) {
          content += word + " ";
          setValue(key, content);
          await new Promise((resolve) => setTimeout(resolve, 30));
        }
      }
    } else {
      if (colorize && (value || value === 0)) {
        element.style.borderWidth = "1px";
        element.style.backgroundColor = "#7A8A69";
        element.style.borderColor = "#0CC00C";
        element.style.color = "white";
        setTimeout(() => {
          element.style.backgroundColor = "#44CF4426";
        }, 300);
      }
      setValue(key, value);
    }
  }, [setValue])

  /**
   * Performs a magic paste on the form
   * 
   * @param {Object} fields The form fields and values
   * @param {Boolean} colorize Whether to colorize the fields
   */
  const magicPasteCallback = useCallback(
    async (fields, colorize = true) => {
      const colorizeFields = async (fields, colorize) => {
        let result = [];

        for (const [key, value] of fields) {
          const element = document.getElementById(key);

          if (element) {
            await handleMagicPasteItem(element, key, value, colorize)
          }

          await new Promise((resolve) => setTimeout(resolve, 200));

          result.push(key);
        }

        return result;
      };

      watch(colorizeFields(fields, colorize));
    },
    [handleMagicPasteItem, watch],
  );

  useEffect(() => {
    setMagicPasteCallback(() => magicPasteCallback);
  }, [magicPasteCallback, setMagicPasteCallback]);

  useEffect(() => {
    const elements = document.getElementsByTagName("input");

    for (const element of elements) {
      if (element.style) {
        element.style.borderColor = "";
        element.style.backgroundColor = "";
        element.style.boxShadow = "";
      }
    }

    if (assistance?.data) {
      Object.entries(assistance?.data).forEach(([key, value]) => {
        setValue(key, value);
      });
    } else {
      reset();
    }
  }, [assistance, reset, setValue]);

  useEffect(() => {
    const subscription = watch((_values, test) => {
      const element = document.getElementById(test.name);

      if (element && element.style.borderColor && test?.type === "change") {
        element.style.borderColor = "";
        element.style.backgroundColor = "";
        element.style.boxShadow = "";
        element.style.color = "";
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * Copies the content to the clipboard
   */
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(remoteAssistance?.genAI?.full_response || "");
    if (!toast.isActive("content-copied")) {
      toast({
        id: "content-copied",
        title: t("beneficiaryFile.form.copied"),
        position: "top",
      });
    }
  }, [remoteAssistance?.genAI?.full_response, toast, t]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onSubmitError)}
      style={{ flexGrow: 1, background: "linear-gradient(#474D4E, #2E363C)", borderRadius: "20px", padding: "8px" }}
    >
      {remoteAssistance?.category !== "ecall" ? (
        <VStack alignItems={"stretch"} gap={"16px"} padding={"8px"}>
          <PrimaryCard title={
            <React.Fragment>
              <Icon as={UserIcon} marginRight={"8px"} />
              {t("beneficiaryFile.beneficiary")}
            </React.Fragment>
          }>
            <BeneficiaryForm errors={errors} register={register} />
          </PrimaryCard>
          <HStack
            alignItems={"stretch"}
            flexWrap={{ base: "wrap-reverse", xl: "nowrap" }}
            gap={"16px"}
          >
            <VStack alignItems={"stretch"} flexGrow={1} gap={"16px"}>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={InterventionIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.interventionAddress")}
                </React.Fragment>
              }>
                <InterventionAddressForm errors={errors} register={register} />
              </PrimaryCard>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={ContractIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.contract")}
                </React.Fragment>
              }>
                <ContractForm errors={errors} register={register} />
              </PrimaryCard>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={FileIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.file")}
                </React.Fragment>
              }>
                <FileForm errors={errors} register={register} />
              </PrimaryCard>
            </VStack>
            <VStack alignItems={"stretch"} flexGrow={1} gap={"16px"}>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={CarIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.vehicle")}
                </React.Fragment>
              }>
                <VehicleForm errors={errors} register={register} />
              </PrimaryCard>
              <Button
                isLoading={isSubmitting}
                size={"md"}
                type={"submit"}
                variant={"secondary"}
                borderRadius={"10px"}
              >
                <Trans>beneficiaryFile.form.next</Trans>
              </Button>
            </VStack>
          </HStack>
        </VStack>
      ) : (
        <VStack width={"full"} alignItems={"stretch"} gap={"16px"} padding={"8px"} flexWrap={{ base: "wrap-reverse", xl: "nowrap" }}>
          <HStack alignItems={"stretch"}
            flexWrap={{ base: "wrap", xl: "nowrap" }}
            gap={"16px"}>
            <VStack gap={"16px"} alignItems={"stretch"} width={"full"}>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={UserIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.caller")}
                </React.Fragment>
              } flexGrow={0}>
                <CallerForm errors={errors} register={register} />
              </PrimaryCard>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={ObservationIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.observation")}
                </React.Fragment>
              } height={"full"} minHeight={"300px"}>
                <ObservationForm errors={errors} register={register} />
              </PrimaryCard>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={VictimIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.victim")}
                </React.Fragment>
              }>
                <VictimForm errors={errors} register={register} />
              </PrimaryCard>
            </VStack>
            <VStack gap={"16px"} alignItems={"stretch"} width={"full"}>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={InterventionIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.interventionAddress")}
                </React.Fragment>
              } flexGrow={1}>
                <InterventionAddressForm errors={errors} register={register} />
              </PrimaryCard>
              <PrimaryCard title={
                <React.Fragment>
                  <Icon as={IntelligentIcon} marginRight={"8px"} />
                  {t("beneficiaryFile.ai")}
                </React.Fragment>
              } height={"full"}>
                <AiForm errors={errors} register={register} copyToClipboard={copyToClipboard} />
              </PrimaryCard>
              <HStack width={"full"} justifyContent={"space-around"} mt={"8px"}>
                <Button
                  isLoading={isSubmitting}
                  size={"md"}
                  type={"submit"}
                  variant={"secondary"}
                  borderRadius={"10px"}
                  width={"full"}
                >
                  <Trans>beneficiaryFile.form.next</Trans>
                </Button>
              </HStack>
            </VStack>
          </HStack>
        </VStack>
      )}
    </form >
  );
};

export default React.memo(BeneficiaryFilePage);
